//@ts-nocheck
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { applyJobApplicationById, fetchAllJobsList, fetchJobDetailsById, fetchJobsDataList, fetchLocationBySearch } from '../../../services/jobService';

interface Job {
  id: string;
  title: string;
  description: string;
}

interface JobState {
  jobs: Job[];
  loading: boolean;
  error: string | null;
  fetchAllJobs: Job[];
  fetchAllJobsLoading: boolean;
  fetchAllJobsError: string | null;
  fetchJobDetailsData: Job | null;
  fetchJobDetailsLoading: boolean;
  fetchJobDetailsError: string | null;
  applyJobApplicationData: Job | null;
  applyJobApplicationLoading: boolean;
  applyJobApplicationError: string | null;
  locationDetails: Board[] | null,
  locationStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  locationStatusError: string | null;

}

const initialState: JobState = {
  jobs: [],
  loading: false,
  error: null,
  fetchAllJobs: [],
  fetchAllJobsLoading: false,
  fetchAllJobsError: null,
  fetchJobDetailsData: null,
  fetchJobDetailsLoading: false,
  fetchJobDetailsError: null,
  applyJobApplicationData: null,
  applyJobApplicationLoading: false,
  applyJobApplicationError: null,
  locationDetails: null,
  locationStatus: 'idle',
  locationStatusError: null,
};


export const fetchJobs = createAsyncThunk<
  Job[], 
  string,
  { rejectValue: string } 
>('jobs/fetchJobs', async (baseSlug, { rejectWithValue }) => {
  try {
    const response = await fetchJobsDataList(baseSlug); 
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to fetch jobs');
  }
});

// fetch all jobs 
// export const fetchAllJobsData = createAsyncThunk<
//   Job[], 
//   string, 
//   { rejectValue: string } 
// >('jobs/fetchAllJobsData', async (baseSlug, { rejectWithValue }) => {
//   try {
//     const response = await fetchAllJobsList(baseSlug); 
//     return response.data;
//   } catch (error: any) {
//     return rejectWithValue(error.response?.data?.message || 'Failed to fetch jobs');
//   }
// });
export const fetchAllJobsData = createAsyncThunk<
  Job[],
  { baseSlug: string; filters?: Record<string, string> },
  { rejectValue: string }
>('jobs/fetchAllJobsData', async ({ baseSlug, filters }, { rejectWithValue }) => {
  try {
    const response = await fetchAllJobsList(baseSlug, filters);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to fetch jobs');
  }
});

// fetch job by id
export const fetchJobDetails = createAsyncThunk<
  Job,
  { baseSlug: string; jobId: string },
  { rejectValue: string }
>('jobs/fetchJobDetails', async ({ baseSlug, jobId }, { rejectWithValue }) => {
  try {
    const response = await fetchJobDetailsById(baseSlug, jobId);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to fetch job details');
  }
});


// apply job application 
// export const applyJobApplication = createAsyncThunk<
//   Job,
//   { baseSlug: string; jobId: string },
//   { rejectValue: string }
// >('jobs/applyJobApplication', async ({ baseSlug, jobId }, { rejectWithValue }) => {
//   try {
//     const response = await applyJobApplicationById(baseSlug, jobId);
//     return response.data;
//   } catch (error: any) {
//     return rejectWithValue(error.response?.data || 'Failed to apply for the job');
//   }
// });

export const applyJobApplication = createAsyncThunk<
  Job,
  { baseSlug: string; jobId: string; token: string | null }, // Accept token
  { rejectValue: string }
>('jobs/applyJobApplication', async ({ baseSlug, jobId, token }, { rejectWithValue }) => {
  try {
    const response = await applyJobApplicationById(baseSlug, jobId, token);
    return response.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || 'Failed to apply for the job');
  }
});


export const fetchLocationByQuery = createAsyncThunk<
  TeamsResponse,
  string,
  { rejectValue: string }
>(
  'location/fetchLocationByQuery',
  async (searchQuery, { rejectWithValue }) => {
    try {
      const response = await fetchLocationBySearch(searchQuery);
      return response;
    } catch (err) {
      const error = err as Error;
      return rejectWithValue(error.message || 'Failed to fetch locations');
    }
  }
);



// Job slice
const jobSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action: PayloadAction<Job[]>) => {
        state.loading = false;
        state.jobs = action.payload;
      })
      .addCase(fetchJobs.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || 'Unknown error';
      })
      .addCase(fetchAllJobsData.pending, (state) => {
        state.fetchAllJobsLoading = true;
        state.fetchAllJobsError = null;
      })
      .addCase(fetchAllJobsData.fulfilled, (state, action: PayloadAction<Job[]>) => {
        state.fetchAllJobsLoading = false;
        state.fetchAllJobs = action.payload;
      })
      .addCase(fetchAllJobsData.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.fetchAllJobsLoading = false;
        state.fetchAllJobsError = action.payload || 'Unknown error';
      })
      .addCase(fetchJobDetails.pending, (state) => {
        state.fetchJobDetailsLoading = true;
        state.fetchJobDetailsError = null;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action: PayloadAction<Job>) => {
        state.fetchJobDetailsLoading = false;
        state.fetchJobDetailsData = action.payload;
      })
      .addCase(fetchJobDetails.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.fetchJobDetailsLoading = false;
        state.fetchJobDetailsError = action.payload || 'Unknown error';
      })
      .addCase(applyJobApplication.pending, (state) => {
        state.applyJobApplicationLoading = true;
        state.applyJobApplicationError = null;
      })
      .addCase(applyJobApplication.fulfilled, (state, action: PayloadAction<Job>) => {
        state.applyJobApplicationLoading = false;
        state.applyJobApplicationData = action.payload;
      })
      .addCase(applyJobApplication.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.applyJobApplicationLoading = false;
        state.applyJobApplicationError = action.payload || 'Unknown error';
      })
      .addCase(fetchLocationByQuery.pending, (state) => {
        state.locationStatus = 'loading';
        state.locationStatusError = null;
      })
      .addCase(fetchLocationByQuery.fulfilled, (state, action) => {
        state.locationStatus = 'succeeded';
        state.locationDetails = action.payload.data;
      })
      .addCase(fetchLocationByQuery.rejected, (state, action) => {
        state.locationStatus = 'failed';
        state.locationStatusError = action.error.message || 'Failed to fetch locations';
      });
  },
});

export default jobSlice.reducer;
